/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { Form as UnformForm } from '@unform/web';
import { FormHandles } from '@unform/core';

import Toolbar from '../../components/Toolbar';
// Replaced MonthPicker with custom MonthStepper in the top bar
import DayPicker from '../../components/DayPicker';
import Input from '../../components/Input';
import Select from '../../components/SelectComponent';
import SwitchComponent from '../../components/SwitchComponent';
import CheckboxInput from '../../components/CheckBox';
import Button from '../../components/Button';
import LoadingAnimation from '../../components/Loading';

import api from '../../services/api';
import formatValue from '../../utils/formatValue';
import { formatDate, parseDateStr } from '../../utils/formatDate';

import {
  Container,
  Form,
  Content,
  HeaderRow,
  Title,
  ControlsRow,
  Search,
  PrimaryButton,
  ToggleGroup,
  ToggleButton,
  Table,
  Actions,
  IconButton,
  InlineFields,
  UniformDate,
  InstallmentsTipWrap,
  MonthStepperWrap,
  ImportRow,
  FileButton,
  FileName,
  DropZone,
  DropText,
  CheckboxRow,
  SubInfo,
  SectionHeader,
  SectionTitle,
  ModalTitle,
  ModalRow,
  ModalFooter,
} from './styles';
import MonthStepper from './MonthStepper';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiAlertCircle,
  FiCreditCard,
  FiDollarSign,
  FiList,
  FiHash,
  FiUpload,
  FiUploadCloud,
} from 'react-icons/fi';
import Tooltip from '../../components/Tooltip';
import { useToast } from '../../hooks/toast';
import * as Yup from 'yup';
import getValidationErrors from '../../utils/getValidationErros';

interface AccountData {
  id: string;
  name: string;
  type: string;
  deleted_at?: string;
}

interface SavingsAccountData {
  id: string;
  name: string;
  deleted_at?: string;
}

interface AccountsSelectOptions {
  value: AccountData | SavingsAccountData;
  label: string;
}

interface Transaction {
  id: string;
  date: string; // dd/MM/yyyy
  account_type: 'IN' | 'OUT';
  transaction_type: 'IN' | 'OUT';
  amount: number;
  account_id: string;
  location: string;
  description: string;
  transfer_id: string | null;
  priority_expense: boolean;
  accountName?: string;
  formattedAmount?: string;
  formattedDate?: string;
}

interface UpdateTransactionData {
  date: Date;
  expenseOrigin: AccountData;
  amount: number;
  location: string;
  description: string;
  opType: boolean;
  priorityExpense: string[];
}

const checkboxOptions = [
  { id: 'priority-expense', value: 'true', label: 'Gasto prioritário' },
];

interface Transfer {
  id: string;
  amount: number;
  origin_account_id: string;
  origin_transaction_id: string;
  destination_savings_account_id: string;
  destination_transaction_id: string;
  date: string; // dd/MM/yyyy
  formattedAmount?: string;
  formattedDate?: string;
  originName?: string;
  destinationName?: string;
}

const TransactionsSummaryV2: React.FC = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState<Date | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'IN' | 'OUT'>('ALL');
  const [priorityOnly, setPriorityOnly] = useState(false);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [originalTransactions, setOriginalTransactions] = useState<
    Transaction[]
  >([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loadingTransfers, setLoadingTransfers] = useState(true);

  // Add/Edit/Delete modal states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editTx, setEditTx] = useState<Transaction | null>(null);
  const [deleteTx, setDeleteTx] = useState<Transaction | null>(null);

  // Form refs
  const addFormRef = useRef<FormHandles>(null);
  const editFormRef = useRef<FormHandles>(null);
  const dateFormRef = useRef<FormHandles>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Transfer modals
  const [isAddTransferOpen, setIsAddTransferOpen] = useState(false);
  const [isEditTransferOpen, setIsEditTransferOpen] = useState(false);
  const [isDeleteTransferOpen, setIsDeleteTransferOpen] = useState(false);
  const [editTransfer, setEditTransfer] = useState<Transfer | null>(null);
  const [deleteTransfer, setDeleteTransfer] = useState<Transfer | null>(null);
  const addTransferFormRef = useRef<FormHandles>(null);
  const editTransferFormRef = useRef<FormHandles>(null);

  // Add Transaction form state
  const [opType, setOpType] = useState(0); // 0 => IN, 1 => OUT
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [accounts, setAccounts] = useState<AccountData[]>([]);
  const [savingsAccounts, setSavingsAccounts] = useState<SavingsAccountData[]>(
    [],
  );
  const [accountsSelectOptions, setAccountsSelectOptions] = useState<
    AccountsSelectOptions[]
  >([]);
  const [outcomeAccountsSelectOptions, setOutcomeAccountsSelectOptions] =
    useState<AccountsSelectOptions[]>([]);
  const [savingsAccountsSelectOptions, setSavingsAccountsSelectOptions] =
    useState<AccountsSelectOptions[]>([]);

  const setNewFilter = useCallback(() => {
    const switchOption = opType;
    setOpType(switchOption === 1 ? 0 : 1);
  }, [opType]);
  const handleOpenAddTransaction = useCallback(() => {
    // Reset state so modal always starts como Entrada
    setOpType(0);
    setIsCheckboxChecked(false);
    setIsAddOpen(true);
  }, []);
  const handleCloseAddTransaction = useCallback(() => {
    // Close and reset flags, avoid resetting unmounted form
    setIsAddOpen(false);
    setOpType(0);
    setIsCheckboxChecked(false);
  }, []);

  // When modal opens, reset the form fields after it mounts
  useEffect(() => {
    if (isAddOpen) {
      addFormRef.current?.reset();
    }
  }, [isAddOpen]);
  const handleCheckbox = useCallback(() => {
    setIsCheckboxChecked((v) => !v);
  }, []);

  const handleDateChange = useCallback((compDate) => {
    setDate(compDate);
  }, []);

  // Fetch transactions for month
  useEffect(() => {
    const currDate = date || new Date();
    let month: any = currDate.getMonth() + 1;
    if (month < 10) month = `0${month}`;
    const year = currDate.getFullYear();
    setLoading(true);
    api
      .get<Transaction[]>('transaction', { params: { month, year } })
      .then((response) => {
        const formatted = response.data
          .sort((a, b) => {
            const dateA = parseDateStr('/', a.date);
            const dateB = parseDateStr('/', b.date);
            return dateA.getTime() - dateB.getTime();
          })
          .map((t) => {
            const d = parseDateStr('/', t.date);
            return {
              ...t,
              formattedAmount: formatValue(t.amount / 100),
              formattedDate: formatDate(d),
            };
          });
        setOriginalTransactions(formatted);
        setTransactions(formatted);
      })
      .finally(() => setLoading(false));

    // Fetch transfers
    setLoadingTransfers(true);
    setTransfers([]); // clear previous transfers during month change
    api
      .get<Transfer[]>('transfer', { params: { month, year } })
      .then((response) => {
        const formatted = response.data
          .sort((a, b) => {
            const dateA = parseDateStr('/', a.date);
            const dateB = parseDateStr('/', b.date);
            return dateA.getTime() - dateB.getTime();
          })
          .map((tr) => {
            const d = parseDateStr('/', tr.date);
            return {
              ...tr,
              formattedAmount: formatValue(tr.amount / 100),
              formattedDate: formatDate(d),
            };
          });
        setTransfers(formatted);
      })
      .catch(() => setTransfers([]))
      .finally(() => setLoadingTransfers(false));
  }, [date]);

  // Search filter by description, location, or account name
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setTransactions(originalTransactions);
      return;
    }
    const filtered = originalTransactions.filter((t) => {
      const hay = `${t.description || ''} ${t.location || ''} ${
        t.accountName || ''
      }`.toLowerCase();
      return hay.includes(q);
    });
    setTransactions(filtered);
  }, [search, originalTransactions]);

  // Fetch accounts for Add form
  useEffect(() => {
    api.get<AccountData[]>('account').then(async (res) => {
      const available = res.data.filter((a) => !a.deleted_at);
      setAccounts(available);
      const sres = await api.get<SavingsAccountData[]>('savings-account');
      const sav = sres.data.filter((a) => !a.deleted_at);
      setSavingsAccounts(sav);
    });
  }, []);

  useEffect(() => {
    const selectAccountsOptions = accounts
      .filter((account) => {
        const op = opType === 1 ? 'OUT' : 'IN';
        return account.type === op;
      })
      .map((option) => ({ value: option, label: option.name }));
    const selectSavingsOptions = savingsAccounts.map((o) => ({
      value: o,
      label: o.name,
    }));
    setAccountsSelectOptions([...selectAccountsOptions, ...selectSavingsOptions]);
    setOutcomeAccountsSelectOptions(
      accounts
        .filter((a) => a.type === 'OUT')
        .map((o) => ({ value: o, label: o.name })),
    );
    setSavingsAccountsSelectOptions(selectSavingsOptions);
  }, [opType, accounts, savingsAccounts]);

  // Actions
  const openEdit = useCallback((tx: Transaction) => {
    setEditTx(tx);
    setIsEditOpen(true);
  }, []);
  const openDelete = useCallback((tx: Transaction) => {
    setDeleteTx(tx);
    setIsDeleteOpen(true);
  }, []);

  const handleUpdateTransaction = useCallback(
    async (data: UpdateTransactionData) => {
      if (!editTx) return;
      try {
        editFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          amount: Yup.number()
            .typeError('Valor deve ser um número')
            .required('Insira um valor numérico para a transação'),
          location: Yup.string()
            .required('Insira um nome para a transação')
            .max(25, 'Máximo de 25 caracteres'),
          description: Yup.string()
            .required('Insira uma descrição para a transação')
            .max(150, 'Máximo de 150 caracteres'),
        });

        await schema.validate(data, { abortEarly: false });

        const d = new Date(data.date);
        let selectedAccountId = editTx.account_id;
        let selectedAccountType: 'IN' | 'OUT' = editTx.account_type as 'IN' | 'OUT';

        if (editTx.transaction_type === 'OUT' && data.expenseOrigin) {
          const selectedAccount = data.expenseOrigin as AccountData;
          selectedAccountId = selectedAccount.id;
          selectedAccountType = 'OUT';
        }

        const payload = {
          id: editTx.id,
          date: formatDate(d),
          transaction_type: data.opType ? 'OUT' : 'IN',
          account_type: selectedAccountType,
          amount: Math.round(Number(data.amount) * 100),
          account_id: selectedAccountId,
          location: data.location,
          description: data.description,
          priority_expense: data.priorityExpense?.length > 0,
        } as any;

        await api.put('transaction', payload);
        setIsEditOpen(false);
        setEditTx(null);
        // reload
        setDate((prev) => (prev ? new Date(prev) : new Date()));
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          editFormRef.current?.setErrors(errors);
          return;
        }
        const errorMessage = err.response?.data?.message;
        if (errorMessage) {
          addToast({ type: 'error', title: 'Erro', description: errorMessage });
        } else {
          addToast({
            type: 'error',
            title: 'Erro na atualização',
            description: 'Houve um erro inesperado, tente novamente mais tarde.',
          });
        }
      }
    },
    [editTx, addToast],
  );

  const handleDeleteTransaction = useCallback(async () => {
    if (!deleteTx) return;
    const formattedDate = deleteTx.date.split('/').join('-');
    await api.delete(`transaction\\${deleteTx.id}`, {
      params: {
        type: deleteTx.account_type,
        date: formattedDate,
      },
    });
    setIsDeleteOpen(false);
    setDeleteTx(null);
    setTransactions((curr) => curr.filter((t) => t.id !== deleteTx.id));
    setOriginalTransactions((curr) => curr.filter((t) => t.id !== deleteTx.id));
  }, [deleteTx]);

  const handleAddTransaction = useCallback(
    async (data: any) => {
      try {
        addFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          account: Yup.string().required('Escolha uma conta'),
          location: Yup.string()
            .required('Insira um nome para a transação')
            .max(25, 'Máximo de 25 caracteres'),
          amount: Yup.number()
            .typeError('Valor deve ser um número')
            .required('Insira um valor numérico para a transação'),
          description: Yup.string()
            .required('Insira uma descrição para a transação')
            .max(150, 'Máximo de 150 caracteres'),
          installments: Yup.number()
            .optional()
            .typeError('Deve ser um número'),
        });

        await schema.validate(data, { abortEarly: false });

        const payload: any = {
          transaction_type: opType === 1 ? 'OUT' : 'IN',
          account_type: data.account.type,
          date: formatDate(new Date(data.date)),
          amount: Math.round(Number(data.amount) * 100),
          account_id: data.account.id,
          location: data.location,
          description: data.description,
          priority_expense: data.priorityExpense?.length > 0,
        };
        if (payload.transaction_type === 'OUT') {
          Object.assign(payload, { installments: Number(data.installments || 1) });
        }

        await api.post('transaction', payload);
        setIsAddOpen(false);
        addFormRef.current?.reset();
        // Reset toggle and checkbox for next open
        setOpType(0);
        setIsCheckboxChecked(false);
        // reload
        setDate((prev) => (prev ? new Date(prev) : new Date()));
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          addFormRef.current?.setErrors(errors);
          return;
        }

        const errorMessage = err.response?.data?.message;
        if (errorMessage === 'Account not found.') {
          addToast({
            type: 'error',
            title: 'Erro no cadastro',
            description: 'Conta não encontrada, tente novamente.',
          });
          return;
        }
        if (errorMessage === 'Account name and operation type do not match') {
          addToast({
            type: 'error',
            title: 'Erro no cadastro',
            description: 'Conta e tipo de operação (entrada/saída) não correspondem.',
          });
          return;
        }
        if (errorMessage === 'Savings account not found.') {
          addToast({
            type: 'error',
            title: 'Erro no cadastro',
            description: 'Poupança não encontrada, tente novamente',
          });
          return;
        }
        addToast({
          type: 'error',
          title: 'Erro no cadastro',
          description: 'Houve um erro inesperado, tente novamente mais tarde.',
        });
      }
    },
    [opType, addToast],
  );

  const rows = useMemo(() => {
    let base = transactions;
    if (typeFilter !== 'ALL') {
      base = base.filter((t) => t.transaction_type === typeFilter);
    }
    if (priorityOnly) {
      base = base.filter((t) => t.transaction_type === 'OUT' && t.priority_expense);
    }
    return base;
  }, [transactions, typeFilter, priorityOnly]);

  const priorityFormatted = useMemo(() => {
    const total = (originalTransactions || []).reduce((acc, t) => {
      if (t.transaction_type === 'OUT' && t.priority_expense) {
        return acc + t.amount;
      }
      return acc;
    }, 0);
    return formatValue(total / 100);
  }, [originalTransactions]);

  // Import CSV state and handlers
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const f = event.target.files?.[0] || null;
      setSelectedFile(f);
      setFileName(f ? f.name : null);
    },
    [],
  );
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f && f.name.toLowerCase().endsWith('.csv')) {
      setSelectedFile(f);
      setFileName(f.name);
    }
  }, []);
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      addToast({ type: 'error', title: 'Erro', description: 'Selecione um arquivo .csv' });
      return;
    }
    setUploading(true);
    try {
      const fileText = await selectedFile.text();
      await api.post('transaction/import', fileText, {
        headers: { 'Content-Type': 'text/csv' },
      });
      addToast({ type: 'success', title: 'Sucesso', description: 'Dados importados com sucesso!' });
      setIsImportOpen(false);
      setSelectedFile(null);
      setFileName(null);
      // refresh list
      setDate((prev) => (prev ? new Date(prev) : new Date()));
    } catch (err) {
      addToast({ type: 'error', title: 'Erro', description: 'Erro ao importar CSV' });
    } finally {
      setUploading(false);
    }
  }, [selectedFile, addToast]);

  // Transfer actions
  const openEditTransfer = useCallback((tr: Transfer) => {
    setEditTransfer(tr);
    setIsEditTransferOpen(true);
  }, []);
  const openDeleteTransfer = useCallback((tr: Transfer) => {
    setDeleteTransfer(tr);
    setIsDeleteTransferOpen(true);
  }, []);

  const handleUpdateTransfer = useCallback(
    async (data: any) => {
      if (!editTransfer) return;
      const transferDate = new Date(data.transferDate);
      const payload = {
        transfer_id: editTransfer.id,
        date: formatDate(transferDate),
        origin_id: data.accountOriginID || data.accountOrigin?.id || editTransfer.origin_account_id,
        destination_id:
          data.destinationSavingsAccountID ||
          data.savingsAccountDestination?.id ||
          editTransfer.destination_savings_account_id,
        amount: Math.round(Number(data.transferAmount) * 100),
      };
      await api.put('transfer', payload);
      setIsEditTransferOpen(false);
      setEditTransfer(null);
      setDate((prev) => (prev ? new Date(prev) : new Date()));
    },
    [editTransfer],
  );

  const handleDeleteTransfer = useCallback(async () => {
    if (!deleteTransfer) return;
    const payload = { transfer_id: deleteTransfer.id, date: deleteTransfer.date };
    await api.delete('transfer', { data: payload });
    setIsDeleteTransferOpen(false);
    setDeleteTransfer(null);
    // Remove transfer from list
    setTransfers((curr) => curr.filter((t) => t.id !== payload.transfer_id));
    // Also remove the two related transactions (origin and destination)
    setTransactions((curr) => curr.filter((t) => t.transfer_id !== payload.transfer_id));
    setOriginalTransactions((curr) => curr.filter((t) => t.transfer_id !== payload.transfer_id));
  }, [deleteTransfer]);

  const handleAddTransfer = useCallback(async (data: any) => {
    const transferDate = new Date(data.transferDate);
    const payload = {
      date: formatDate(transferDate),
      origin_id: data.accountOrigin?.id,
      destination_id: data.savingsAccountDestination?.id,
      amount: Math.round(Number(data.transferAmount) * 100),
    };
    await api.post('transfer', payload);
    setIsAddTransferOpen(false);
    addTransferFormRef.current?.reset();
    setDate((prev) => (prev ? new Date(prev) : new Date()));
  }, []);

  return (
    <Container ref={containerRef}>
      <Toolbar />
      {/* Top controls bar: search + type filter + month stepper */}
      {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
      <Form ref={dateFormRef} onSubmit={() => {}}>
        {/* Keeping form wrapper for consistent spacing */}
      </Form>

      <Content>
        <HeaderRow>
          <Title>Transações</Title>
        </HeaderRow>
        <SubInfo>Gastos prioritários: {priorityFormatted}</SubInfo>
        <ControlsRow>
          <Search
            placeholder="Busque por descrição, local ou conta"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* Type filter buttons */}
          <ToggleGroup className="filtersGroup">
            <ToggleButton
              active={typeFilter === 'IN'}
              tone="in"
              onClick={() =>
                setTypeFilter((cur) => (cur === 'IN' ? 'ALL' : 'IN'))
              }
            >
              Entrada
            </ToggleButton>
            <ToggleButton
              active={typeFilter === 'OUT'}
              tone="out"
              onClick={() =>
                setTypeFilter((cur) => (cur === 'OUT' ? 'ALL' : 'OUT'))
              }
            >
              Saída
            </ToggleButton>
            <ToggleButton
              active={priorityOnly}
              tone="out"
              onClick={() => setPriorityOnly((cur) => !cur)}
            >
              Prioritário
            </ToggleButton>
          </ToggleGroup>
          {/* Month stepper */}
          <MonthStepperWrap>
            <MonthStepper
              date={date || new Date()}
              onPrev={() => {
                const d = new Date(date || new Date());
                d.setMonth(d.getMonth() - 1);
                d.setDate(1);
                setDate(d);
              }}
              onNext={() => {
                const d = new Date(date || new Date());
                d.setMonth(d.getMonth() + 1);
                d.setDate(1);
                setDate(d);
              }}
            />
          </MonthStepperWrap>
          <PrimaryButton variant="primary" onClick={handleOpenAddTransaction}>
            <FiPlus size={16} /> Adicionar transação
          </PrimaryButton>
          <PrimaryButton className="importBtn" variant="primary" onClick={() => setIsImportOpen(true)}>
            <FiUpload size={16} /> Importar CSV
          </PrimaryButton>
        </ControlsRow>

        {loading ? (
          <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingAnimation />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Conta</th>
                <th>Local</th>
                <th>Descrição</th>
                <th>Prioritário</th>
                <th>Valor</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td data-label="Data">{t.formattedDate || t.date}</td>
                  <td data-label="Conta">{t.accountName}</td>
                  <td data-label="Local">{t.location || '-'}</td>
                  <td data-label="Descrição">{t.description || '-'}</td>
                  <td data-label="Prioritário">{t.transaction_type === 'OUT' && t.priority_expense ? 'Sim' : 'Não'}</td>
                  <td data-label="Valor" className={`amount ${t.transaction_type === 'OUT' ? 'out' : 'in'}`}>
                    {t.formattedAmount}
                  </td>
                  <td data-label="Ações">
                    {t.transfer_id ? (
                      <Tooltip title="Transferências entre contas devem ser editadas na sessão abaixo">
                        <FiAlertCircle color="#FFC107" size={18} />
                      </Tooltip>
                    ) : (
                      <Actions>
                        <IconButton onClick={() => openEdit(t)} aria-label="Edit">
                          <FiEdit size={16} />
                        </IconButton>
                        <IconButton
                          tone="danger"
                          onClick={() => openDelete(t)}
                          aria-label="Delete"
                        >
                          <FiTrash2 size={16} />
                        </IconButton>
                      </Actions>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Content>

      {/* Transfers Section */}
      <Content>
        <SectionHeader>
          <SectionTitle>Transferências</SectionTitle>
          <PrimaryButton variant="primary" onClick={() => setIsAddTransferOpen(true)}>
            <FiPlus size={16} /> Adicionar transferência
          </PrimaryButton>
        </SectionHeader>

        {loadingTransfers ? (
          <div style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingAnimation />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Conta de origem</th>
                <th>Poupança destino</th>
                <th>Valor transferido</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((tr) => (
                <tr key={tr.id}>
                  <td data-label="Data">{tr.formattedDate || tr.date}</td>
                  <td data-label="Conta de origem">{tr.originName}</td>
                  <td data-label="Poupança destino">{tr.destinationName}</td>
                  <td data-label="Valor" className="amount xfer">{tr.formattedAmount}</td>
                  <td data-label="Ações">
                    <Actions>
                      <IconButton onClick={() => openEditTransfer(tr)} aria-label="Edit">
                        <FiEdit size={16} />
                      </IconButton>
                      <IconButton tone="danger" onClick={() => openDeleteTransfer(tr)} aria-label="Delete">
                        <FiTrash2 size={16} />
                      </IconButton>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Content>

      {/* Add Transaction Modal */}
      <Modal
        open={isAddOpen}
        onClose={handleCloseAddTransaction}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Nova transação</ModalTitle>
        <UnformForm ref={addFormRef} onSubmit={handleAddTransaction}>
          <SwitchComponent
            name="opType"
            options={['Entrada', 'Saída']}
            id="op-type"
            clickEvent={setNewFilter}
          />
          <UniformDate>
            <DayPicker name="date" />
          </UniformDate>
          {opType === 1 && (
            <CheckboxRow>
              <CheckboxInput
                infoTitle="Gastos prioritários são somados e mostrados por mês na tela de transações."
                name="priorityExpense"
                options={checkboxOptions}
                onChange={handleCheckbox}
              />
            </CheckboxRow>
          )}
          <Select
            name="account"
            icon={FiCreditCard}
            placeholder="Conta"
            selectOptions={accountsSelectOptions}
          />
          {opType === 1 ? (
            <InlineFields>
              <Input name="amount" placeholder="Valor" icon={FiDollarSign} />
              <InstallmentsTipWrap>
                <Input
                  name="installments"
                  placeholder="Parcelas"
                  defaultValue={1}
                  hasTip="Número de parcelas dessa transação"
                  icon={FiHash}
                />
              </InstallmentsTipWrap>
            </InlineFields>
          ) : (
            <Input name="amount" placeholder="Valor" icon={FiDollarSign} />
          )}
          <Input name="location" placeholder="Local" icon={FiList} />
          <Input name="description" placeholder="Descrição" icon={FiList} />
          <ModalFooter>
            <Button type="button" onClick={handleCloseAddTransaction}>
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      {/* Import CSV Modal */}
      <Modal
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Importar planilha com transações</ModalTitle>
        <div>
          <ImportRow>
            <input
              ref={fileInputRef}
              id="csvFileInput"
              style={{ display: 'none' }}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
            <DropZone
              active={isDragging}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <FiUploadCloud size={24} />
              <DropText>
                Arraste e solte o arquivo .csv aqui
                <br />
                ou clique para selecionar
              </DropText>
              {fileName && <FileName>Arquivo selecionado: {fileName}</FileName>}
            </DropZone>
          </ImportRow>
          <ModalFooter>
            <Button type="button" onClick={() => setIsImportOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Enviando...' : 'Enviar planilha'}
            </Button>
          </ModalFooter>
        </div>
      </Modal>

  {/* Edit Transaction Modal */}
      <Modal
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Editar transação</ModalTitle>
        {editTx && (
          <UnformForm ref={editFormRef} onSubmit={handleUpdateTransaction}>
            <SwitchComponent
              name="opType"
              options={['Entrada', 'Saída']}
              id="op-type"
              isChecked={editTx.transaction_type === 'OUT'}
              isDisabled
            />
            <UniformDate>
              <DayPicker name="date" defaultDate={parseDateStr('/', editTx.date)} />
            </UniformDate>
            {editTx.transaction_type === 'OUT' && (
              <CheckboxRow>
                <CheckboxInput
                  infoTitle="Gastos prioritários são somados e mostrados por mês na tela de transações."
                  name="priorityExpense"
                  options={checkboxOptions}
                  onChange={handleCheckbox}
                  defaultChecked={editTx.priority_expense}
                />
              </CheckboxRow>
            )}
            <Select
              name="expenseOrigin"
              icon={FiCreditCard}
              placeholder="Conta"
              selectOptions={
                editTx.transaction_type === 'OUT'
                  ? outcomeAccountsSelectOptions
                  : accountsSelectOptions
              }
              defaultValue={{
                value: {
                  id: editTx.account_id,
                  type: editTx.account_type,
                  name: editTx.accountName,
                },
                label: editTx.accountName || '',
              }}
              isDisabled={editTx.transaction_type !== 'OUT'}
            />
            <Input name="location" placeholder="Local" defaultValue={editTx.location} icon={FiList} />
            <Input
              name="amount"
              placeholder="Valor"
              defaultValue={editTx ? editTx.amount / 100 : 0}
              icon={FiDollarSign}
            />
            <Input
              name="description"
              placeholder="Descrição"
              defaultValue={editTx.description}
              icon={FiList}
            />
            <ModalFooter>
              <Button type="button" onClick={() => setIsEditOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar alterações</Button>
            </ModalFooter>
          </UnformForm>
        )}
      </Modal>

  {/* Delete Transaction Modal */}
      <Modal
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Tem certeza que deseja deletar a transação?</ModalTitle>
        {deleteTx && (
          <div>
            <ModalRow>
              <strong>Data:</strong> <span>{deleteTx.date}</span>
            </ModalRow>
            <ModalRow>
              <strong>Conta:</strong> <span>{deleteTx.accountName}</span>
            </ModalRow>
            <ModalRow>
              <strong>Valor:</strong> <span>{deleteTx.formattedAmount}</span>
            </ModalRow>
            <ModalRow>
              <strong>Local:</strong> <span>{deleteTx.location}</span>
            </ModalRow>
            <ModalRow>
              <strong>Descrição:</strong> <span>{deleteTx.description}</span>
            </ModalRow>
          </div>
        )}
        <ModalFooter>
          <Button type="button" onClick={() => setIsDeleteOpen(false)}>
            Cancelar
          </Button>
          <Button type="submit" onClick={handleDeleteTransaction}>
            Excluir transação
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add Transfer Modal */}
      <Modal
        open={isAddTransferOpen}
        onClose={() => setIsAddTransferOpen(false)}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Nova transferência</ModalTitle>
        <UnformForm ref={addTransferFormRef} onSubmit={handleAddTransfer}>
          <UniformDate>
            <DayPicker name="transferDate" />
          </UniformDate>
          <Select
            name="accountOrigin"
            icon={FiCreditCard}
            placeholder="Conta"
            selectOptions={outcomeAccountsSelectOptions}
          />
          <Select
            name="savingsAccountDestination"
            icon={FiCreditCard}
            placeholder="Poupança"
            selectOptions={savingsAccountsSelectOptions}
          />
          <Input name="transferAmount" placeholder="Valor" icon={FiDollarSign} />
          <ModalFooter>
            <Button type="button" onClick={() => setIsAddTransferOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      {/* Edit Transfer Modal */}
      <Modal
        open={isEditTransferOpen}
        onClose={() => setIsEditTransferOpen(false)}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Editar transferência</ModalTitle>
        {editTransfer && (
          <UnformForm ref={editTransferFormRef} onSubmit={handleUpdateTransfer}>
            <UniformDate>
              <DayPicker
                name="transferDate"
                defaultDate={parseDateStr('/', editTransfer.date)}
              />
            </UniformDate>
            <Select
              name="accountOrigin"
              icon={FiCreditCard}
              placeholder="Conta"
              selectOptions={outcomeAccountsSelectOptions}
              defaultValue={{
                value: editTransfer.origin_account_id,
                label: editTransfer.originName || '',
              }}
            />
            <Select
              name="savingsAccountDestination"
              icon={FiCreditCard}
              placeholder="Poupança"
              selectOptions={savingsAccountsSelectOptions}
              defaultValue={{
                value: editTransfer.destination_savings_account_id,
                label: editTransfer.destinationName || '',
              }}
            />
            <Input
              name="transferAmount"
              placeholder="Valor"
              defaultValue={editTransfer ? editTransfer.amount / 100 : 0}
              icon={FiDollarSign}
            />
            <ModalFooter>
              <Button type="button" onClick={() => setIsEditTransferOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar alterações</Button>
            </ModalFooter>
          </UnformForm>
        )}
      </Modal>

      {/* Delete Transfer Modal */}
      <Modal
        open={isDeleteTransferOpen}
        onClose={() => setIsDeleteTransferOpen(false)}
        showCloseIcon={false}
        center
        classNames={{ modal: 'v2Modal' }}
        container={containerRef.current as Element}
      >
        <ModalTitle>Tem certeza que deseja deletar a transferência?</ModalTitle>
        {deleteTransfer && (
          <div>
            <ModalRow>
              <strong>Data:</strong> <span>{deleteTransfer.date}</span>
            </ModalRow>
            <ModalRow>
              <strong>Conta:</strong> <span>{deleteTransfer.originName}</span>
            </ModalRow>
            <ModalRow>
              <strong>Poupança:</strong>{' '}
              <span>{deleteTransfer.destinationName}</span>
            </ModalRow>
            <ModalRow>
              <strong>Valor:</strong>{' '}
              <span>{deleteTransfer.formattedAmount}</span>
            </ModalRow>
          </div>
        )}
        <ModalFooter>
          <Button type="button" onClick={() => setIsDeleteTransferOpen(false)}>
            Cancelar
          </Button>
          <Button type="submit" onClick={handleDeleteTransfer}>
            Excluir transferência
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default TransactionsSummaryV2;
